import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { BasePageComponent } from '@shared/components/base-page/base-page.component';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { UpperCasePipe } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { DataService } from '@services/data.service';
import { DataCategoryType } from 'src/app/enums/data-category-type';
import { TranslateModule } from '@ngx-translate/core';
import { UserRole } from 'src/app/enums/user-role';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { CodeTeamService } from '@services/code-team.service';
import { ShiftSchedulerPopoverEditComponent } from './components/popover-edit/shift-scheduler-popover-edit.component';
import { EditShiftModalComponent } from '@shared/components/molecules/tab-container/components/edit-shift.modal/edit-shift-modal.component';
import { Shift } from '@interfaces/data/shift';
import { ShiftRequest } from '@interfaces/data/shift-request';
import { DialogAction } from '@features/dashboard/components/alert/enums/dialogAction';
import { CancelBtn } from '@shared/components/cancel-btn/cancel-btn.component';
import { SaveBtn } from '@shared/components/save-btn/save-btn.component';
import { ShiftForm } from '@interfaces/shift/shift-form';
import { ShiftAssignmentResponse } from '@interfaces/shift/shift-assignment-response';
import { AssignmentService } from '@services/assignment.service';
import { ToastService } from '@services/toast.service';
import { SecurityAssignmentService } from '@services/security-assignment.service';

export enum WeekDay {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

@Component({
  selector: 'app-staff-scheduler',
  standalone: true,
  imports: [
    BasePageComponent,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    UpperCasePipe,
    LoadingSpinnerComponent,
    ShiftSchedulerPopoverEditComponent,
    CancelBtn,
    SaveBtn,
  ],
  templateUrl: './staff-scheduler.component.html',
  styleUrl: './staff-scheduler.component.scss',
})
export class StaffSchedulerComponent implements OnInit {
  @ViewChildren(ShiftSchedulerPopoverEditComponent)
  popovers: QueryList<ShiftSchedulerPopoverEditComponent> | undefined;
  weekDays = [
    { label: WeekDay.MONDAY },
    { label: WeekDay.TUESDAY },
    { label: WeekDay.WEDNESDAY },
    { label: WeekDay.THURSDAY },
    { label: WeekDay.FRIDAY },
    { label: WeekDay.SATURDAY },
    { label: WeekDay.SUNDAY },
  ];
  private dataService = inject(DataService);
  private codeTeamService = inject(CodeTeamService);
  private assignmentService = inject(AssignmentService);
  private securityAssignmentService = inject(SecurityAssignmentService);
  private modalService = inject(NgbModal);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);
  private initialShiftAssignments: ShiftAssignmentResponse[] = [];
  isInitialLoad = signal<boolean>(true);
  isSaving = signal<boolean>(false);
  parentForm?: FormGroup;
  filteredTeamMembers = computed(() => {
    return this.codeTeamService
      .teamMembers()
      .filter((tm) => [UserRole.SECURITY_STAFF].includes(tm.role));
  });

  get shifts() {
    return this.parentForm?.get('shifts') as FormArray;
  }

  weekDay = WeekDay;
  role = UserRole;

  getWeeDay(shiftIndex: number, weekDay: WeekDay) {
    return this.shifts?.at(shiftIndex).get(weekDay) as FormArray;
  }

  constructor() {
    effect(() => {
      const assignments: ShiftAssignmentResponse[] =
        this.securityAssignmentService
          .securityAssignments()
          .slice()
          .sort((a, b) => a.shift.shiftNumber - b.shift.shiftNumber);
      this.initialShiftAssignments = assignments;
      this.mapAndInitForm(assignments);
      if (assignments.length > 0) {
        this.isInitialLoad.set(false);
      }
    });

    setTimeout(() => {
      this.isInitialLoad.set(false);
    }, 2000);
  }

  private loadData() {
    this.codeTeamService.sendStaffDataRequest();
    this.dataService.sendDataRequest([
      {
        category: DataCategoryType.SECURITY_ASSIGNMENT,
        filters: {},
      },
    ]);
  }

  private mapAndInitForm(assignments: ShiftAssignmentResponse[]) {
    const shiftGroups = assignments.map((a) => {
      const mondayGroups = a.assignments.monday.map((m) =>
        this.fb.group({ ...m }),
      );
      const tuesdayGroups = a.assignments.tuesday.map((t) =>
        this.fb.group({ ...t }),
      );
      const wednesdayGroups = a.assignments.wednesday.map((w) =>
        this.fb.group({ ...w }),
      );
      const thursdayGroups = a.assignments.thursday.map((t) =>
        this.fb.group({ ...t }),
      );
      const fridayGroups = a.assignments.friday.map((f) =>
        this.fb.group({ ...f }),
      );
      const saturdayGroups = a.assignments.saturday.map((st) =>
        this.fb.group({ ...st }),
      );
      const sundayGroups = a.assignments.sunday.map((sn) =>
        this.fb.group({ ...sn }),
      );
      return this.fb.group({
        shiftNumber: a.shift.shiftNumber,
        startTime: a.shift.startTime,
        endTime: a.shift.endTime,
        monday: this.fb.array(mondayGroups),
        department: a.shift.department,
        tuesday: this.fb.array(tuesdayGroups),
        wednesday: this.fb.array(wednesdayGroups),
        thursday: this.fb.array(thursdayGroups),
        friday: this.fb.array(fridayGroups),
        saturday: this.fb.array(saturdayGroups),
        sunday: this.fb.array(sundayGroups),
      });
    });
    this.parentForm = this.fb.group({
      shifts: this.fb.array(shiftGroups),
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  handlePopoverSelection(popover: NgbPopover) {
    if (this.popovers) {
      this.popovers.forEach((p) => {
        p.handleCancel();
        p.closePopover();
      });
    }
    popover.open();
  }

  openEditShiftModal(shiftRequest: ShiftRequest) {
    const ngbModalRef = this.modalService.open(EditShiftModalComponent, {
      centered: true,
      backdrop: true,
      size: 'lg',
    });
    const mappedShift: Shift = {
      id: shiftRequest.id,
      startTime: {
        hour: Number(shiftRequest.startTime.split(':')[0]),
        minute: Number(shiftRequest.startTime.split(':')[1]),
      },
      endTime: {
        hour: Number(shiftRequest.endTime.split(':')[0]),
        minute: Number(shiftRequest.endTime.split(':')[1]),
      },
      shiftNumber: shiftRequest.shiftNumber,
      department: shiftRequest.department,
    };
    ngbModalRef.componentInstance.shift = signal(mappedShift);
    ngbModalRef.componentInstance.modalRef = signal(ngbModalRef);

    ngbModalRef.result.then((res: { action: DialogAction; payload: Shift }) => {
      if (DialogAction.SUBMIT === res.action) {
        this.saveChanges(res.payload);
      }
    });
  }

  saveChanges(shift?: Shift) {
    this.isSaving.set(true);
    this.parentForm?.disable();
    const form: ShiftForm = this.parentForm?.value;
    this.assignmentService.sendSecurityRequest(form.shifts, shift);
    setTimeout(() => {
      this.loadData();
      this.parentForm?.enable();
      this.toastService.showSuccessMessage('front.toast.data-updated');
      this.isSaving.set(false);
    }, 1000);
  }

  resetForm() {
    this.mapAndInitForm(this.initialShiftAssignments);
  }

  removeAssignment(
    shiftIndex: number,
    weekDay: WeekDay,
    assignmentIndex: number,
  ) {
    let shiftArray = this.parentForm?.get('shifts') as FormArray;
    let dayAssignments = shiftArray.at(shiftIndex).get(weekDay) as FormArray;
    dayAssignments.removeAt(assignmentIndex);
  }
}
